<script setup lang="ts">
import { useScriptTag } from "@vueuse/core";
import { CalendarX2, ExternalLink } from "lucide-vue-next";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type { BookingPage, BookingPageEventType } from "@/types/BookingPage";

const props = defineProps<{
    bookingPage: BookingPage & { event_types: BookingPageEventType[] };
    mobile: boolean;
}>();

const hasActiveEventTypes = computed(() =>
    props.bookingPage.event_types.some((eventType) => !eventType.disabled),
);
const containerId = `zaptime-preview-${crypto.randomUUID()}`;
const scriptLoaded = ref(false);

const { load } = useScriptTag(
    "https://iframe.zaptime.app/zaptime.js",
    undefined,
    { manual: true },
);

onMounted(async () => {
    await load();
    scriptLoaded.value = true;
});

let scrollbarObserver: MutationObserver | undefined;

function disableIframeScrollbars(container: HTMLElement): void {
    const apply = (): void => {
        container.querySelectorAll("iframe").forEach((iframe) => {
            iframe.setAttribute("scrolling", "no");
        });
    };

    apply();
    scrollbarObserver?.disconnect();
    scrollbarObserver = new MutationObserver(apply);
    scrollbarObserver.observe(container, { childList: true, subtree: true });
}

onBeforeUnmount(() => {
    scrollbarObserver?.disconnect();
});

function renderWidget(): void {
    const container = document.getElementById(containerId);
    const zaptime = (
        window as typeof window & {
            Zaptime?: (options: {
                type: string;
                config: { token: string };
            }) => { render: (selector: string) => void };
        }
    ).Zaptime;

    if (!container || !zaptime) return;

    container.innerHTML = "";
    disableIframeScrollbars(container);
    zaptime({
        type: "group",
        config: { token: props.bookingPage.token },
    }).render(`#${containerId}`);
}

watch(
    [scriptLoaded, hasActiveEventTypes, () => props.bookingPage],
    () => {
        if (scriptLoaded.value && hasActiveEventTypes.value) renderWidget();
    },
    { flush: "post" },
);
</script>

<template>
    <div
        class="flex w-full flex-col transition-[max-width] duration-200"
        :class="mobile ? 'max-w-[375px]' : 'max-w-none'"
    >
        <div v-if="hasActiveEventTypes" :id="containerId" class="w-full" />
        <div
            v-else
            class="rounded-xl border-iron-200 bg-white px-5 py-10 flex flex-col items-center border text-center"
        >
            <div
                class="h-14 w-14 bg-iron-100 flex items-center justify-center overflow-hidden rounded-full"
            >
                <img
                    v-if="bookingPage.avatar"
                    :src="bookingPage.avatar"
                    alt=""
                    class="h-full w-full object-cover"
                />
                <CalendarX2 v-else :size="22" class="text-iron-400" />
            </div>
            <p class="mt-4 text-sm font-medium text-iron-800">
                {{ $t("my_page.public_empty.title") }}
            </p>
            <p class="mt-1 text-xs leading-5 text-iron-500">
                {{
                    $t("my_page.editor.empty_preview", {
                        team: bookingPage.team.name,
                    })
                }}
            </p>
        </div>
        <a
            :href="bookingPage.public_url"
            target="_blank"
            rel="noopener noreferrer"
            class="gap-2 pt-8 text-xs font-medium text-iron-500 hover:text-iron-800 mt-auto flex items-center justify-center"
        >
            {{ $t("my_page.editor.open_live_page") }}
            <ExternalLink :size="13" />
        </a>
    </div>
</template>
